import Minze from 'minze'
import axios from 'redaxios'
import { LvcRecommendationData } from '../../types/global'
import { config } from '../constants'
import { AuthModule, FavoriteItem } from './auth'
import { getFromCache } from './cache'
import { Emitter } from './emitter'
import { FlowManager, IFlowManager } from './epflow'
import { Geo } from './geo'
import { Locale } from './locale'
import { ConsoleError, ConsoleInfo } from './logging'
import { Routing } from './routing'
import { SearchManager } from './search'
import { URLRegionSearchSubscriber } from './search/URLRegionSearchSubscriber'
import { SquidexTextLabels } from './squidex/labels'
import { TabAccountant } from './tabAccountant'
import { Attribute, canShowOnThisTab, delay } from './utils'

Locale.getInstance().registerKeySet('fr', 'mainModule', {
  accountDeletedMessage: 'Votre compte a bien été supprimé',
  emailChangeSuccess: 'Votre adresse e-mail a été modifiée',
})

Locale.getInstance().registerKeySet('en', 'mainModule', {
  accountDeletedMessage: 'Your account has been deleted',
  emailChangeSuccess: 'Your email has been changed',
})

export default class MainModule {
  private static instance: MainModule
  public static getInstance(): MainModule {
    if (!MainModule.instance) {
      MainModule.instance = new MainModule()
    }
    return MainModule.instance
  }
  public static setInstance(instance: MainModule): void {
    MainModule.instance = instance
  }
  private _tabAccountant = TabAccountant.getInstance()
  private _isLoggedIn = false
  private _ee: Emitter = new Emitter()
  private _auth: AuthModule | null = null
  private _user: any = null
  private _lang = 'en'
  private _routing: Routing = new Routing()
  private _searchManager = new SearchManager()
  private _flowManager = new FlowManager()
  private _locale = Locale.getInstance()
  private _favorites = null
  private _geo: Geo | null = null
  private _favoritesLoading: boolean = false
  private _datepickerContainer: HTMLElement | null = null
  private _recommendedContent: LvcRecommendationData[] | null = null
  private _satisfactionPopupTimer: number | null = null
  private _satisfactionPopupShown = false
  private _satisfactionSecondaryTimer: number | null = null
  private _geolocationAnalyticsScheduled = false

  constructor() {
    this._lang = this.getLocale().getLanguage()
    this._routing.setLanguage(this._lang)
    this._auth = new AuthModule(this.getEE(), this)
    new SquidexTextLabels(this.getEE()).initialize()
    this.initialize()
    MainModule.setInstance(this)
  }

  public async initialize(): Promise<void> {
    window.addEventListener('DOMContentLoaded', this.onDocumentReady.bind(this))

    //TODO: initialize module
    this._ee.on('lvc:user_state_changed', this.onAuthStateChanged.bind(this))
    this._ee.on('lvc:finished_signing_in:success', this.onSignIn.bind(this))
    this._ee.on('lvc:user_logged_out', this.onUserLoggedOut.bind(this))
    if (!this._auth) this._auth = new AuthModule(this.getEE(), this)

    // Create Geo and schedule geolocation analytics early, independent of auth
    // Wrapped in try-catch to ensure geo failures don't break initialization
    try {
      this._geo = new Geo(this.getEE(), this)
      this.scheduleGeolocationAnalyticsTracking()
    } catch (error) {
      ConsoleError(true, 'Failed to initialize Geo module (non-blocking)', error)
    }

    await this._auth.initialize()
    const urlParams = new URLSearchParams(window.location.search)
    const accountDeleted = urlParams.get('account-deleted')
    if (accountDeleted === 'true')
      this.showToastAlert(Locale.getKey('mainModule.accountDeletedMessage'))

    const emailChangingJob = urlParams.get('email_change_token')
    if (emailChangingJob && emailChangingJob.trim().length > 0) {
      axios
        .post(`${config.backendApi}/user/auth/confirm-email-change/${emailChangingJob}`)
        .then(() => {
          this.showToastAlert(Locale.getKey('mainModule.emailChangeSuccess'))
          this._auth?.logout()
        })
        .catch(e => {
          console.log(e)
        })
    }

    // Initialize satisfaction popup cross-tab coordination
    this.initializeSatisfactionPopup()

    setTimeout(() => {
      this.deferedInitialize()
    }, 0)
  }

  // executes on next callstack iteration, good for things that reference main module right away
  public async deferedInitialize(): Promise<void> {
    this.getSearchManager().subscribe(new URLRegionSearchSubscriber('searchByRegion'))
  }

  onDocumentReady(): void {
    this._datepickerContainer = this.injectIntoDOM('lvc-datepicker-container', [
      {
        name: 'name',
        value: 'lvc-datepicker-container',
      },
    ])
    // Run function to handle eventClickedFromList for GTM event
    this.dumpAndClearEventClickedFromList()

    // Initialize satisfaction popup timer (only if feature flag is enabled)
    if (this.isSatisfactionPopupEnabled()) {
      this.scheduleSatisfactionPopup()
    }
  }

  private async onAuthStateChanged(data: any): Promise<void> {
    let routing = this.getRouting()
    // only to update currently focused or the only tab
    if (!canShowOnThisTab()) {
      return
    }

    Minze.cast('lvc:user_state_changed', data)
    if (data?.user) {
      this.analyticsSetCustomData({
        user_id: data.user.uid,
      })

      // Algolia insights setAuthenticatedUserToken
      if ((window as any).algoliaInsights) {
        try {
          ;(window as any).algoliaInsights('setAuthenticatedUserToken', data?.user.uid)
        } catch (error) {
          console.error('Error in Algolia insights setAuthenticatedUserToken API:', error)
        }
      }
    }

    await this.setRecommendedContent()
    let isProspace = await this.getAuth().isPartner()

    routing.guardCurrentProspaceRoute(data.isLoggedIn, isProspace)

    // if the user is on the profile/signup page, we don't want to show the popup, he should already be aware he needs to complete his profile
    if (
      routing.getRouteKeyFromCurrentLocation() === 'profile' ||
      routing.getRouteKeyFromCurrentLocation() === 'signup' ||
      routing.getRouteKeyFromCurrentLocation() === 'login'
    ) {
      return
    }

    await delay(2000) // wait for the routing to finish, otherwise the popup will fail to show

    // To remind the user to complete their profile, we first check if they have already seen the popup
    if (sessionStorage.getItem('hasSeenCompleteProfilePopup') !== 'true') {
      // check if the user has completed their profile, if not, show the complete profile popup
      let isProfileCompleted = await this.getAuth().isProfileCompleted()
      if (!isProfileCompleted) {
        this.showCompleteProfilePopup()
      }
    }
  }

  // function that injects the "complete profile" popup into the DOM
  private async showCompleteProfilePopup() {
    const completeProfilePopup = document.createElement('lvc-complete-profile-popup')
    this.injectIntoDOM('lvc-reminder-complete-profile-popup')
  }

  private async onUserLoggedOut(): Promise<void> {
    let shouldGoToLastPage = getFromCache('shouldGoToLastPage', true)

    if (shouldGoToLastPage) this.getRouting().goToLastPage()
  }

  private async onSignIn(data: any) {
    const lang = this.getLocale().getLanguage()
    const routing = this.getRouting()
    let userLanguage = this.getAuth().getUserLanguage()

    if (userLanguage) {
      routing.setLanguage(userLanguage)
    }
    // If there is a redirectTo in the data, go there, override all else.

    if (data?.redirectTo) {
      routing.goTo(data.redirectTo)
    } else if (data?.goToAccount) {
      routing.goTo(Routing.ROUTES[lang].profile)
    } else {
      routing.goToLastPage()
    }
  }

  public getAuth(): AuthModule {
    if (!this._auth) {
      this._auth = new AuthModule(this._ee, this)
    }
    return this._auth
  }

  public getRouting(): Routing {
    return this._routing
  }

  public getFlowManager(): IFlowManager {
    return this._flowManager
  }

  public getSearchManager(): SearchManager {
    return this._searchManager
  }

  public getLang(): any {
    return this._lang
  }

  public getLocale(): Locale {
    return this._locale
  }
  public async setRecommendedContent(): Promise<void> {
    try {
      const response = await axios.get(config.recommendedContentFile)
      const lang = Locale.language
      if (!response || !response.data || response.data[lang].length < 1) {
        return
      }
      let responseData = response.data[lang] as LvcRecommendationData[]
      const authenticated = MainModule.getInstance().getAuth().isLoggedIn()
      const filteredData = responseData.filter(item => {
        if (authenticated) {
          return item.displayPreference !== 'not-authenticated'
        } else {
          return item.displayPreference !== 'authenticated'
        }
      })
      this._recommendedContent = filteredData
      Minze.cast('lvc:recommended_content_updated')
    } catch (err) {
      console.error('Error getting recommended content')
    }
  }
  public getRecommendedContent(): LvcRecommendationData[] | null {
    return this._recommendedContent
  }

  public getEE(): Emitter {
    if (!this._ee) {
      this._ee = new Emitter()
    }
    return this._ee
  }

  public getGeo(): Geo | null {
    try {
      if (!this._geo) {
        this._geo = new Geo(this.getEE(), this)
        this.scheduleGeolocationAnalyticsTracking()
      }
      return this._geo
    } catch (error) {
      ConsoleError(true, 'Failed to get Geo instance (non-blocking)', error)
      return null
    }
  }

  public getDatepickerContainer(): HTMLElement | null {
    return this._datepickerContainer
  }

  /* #endregion */

  public showModal(modalId: string, data?: any): void {
    Minze.cast('lvc:toggle-modal', {
      status: 'show',
      modalId: modalId,
      ...data,
    })
  }

  public hideModal(modalId: string): void {
    Minze.cast('lvc:toggle-modal', { status: 'hide', modalId: modalId })
  }

  public toggleModal(modalId: string): void {
    Minze.cast('lvc:toggle-modal', { modalId: modalId })
  }

  public injectIntoDOM(tag: string, attributes?: Attribute[]) {
    const el = document.createElement(tag)

    if (attributes) {
      for (let k = 0; k < attributes.length; k++) {
        el.setAttribute(attributes[k].name, attributes[k].value)
      }
    }

    document.body.appendChild(el)
    return el
  }

  public async loadFavoritesItems(favoriteData: FavoriteItem[]) {
    if (this._favoritesLoading) return
    this._favoritesLoading = true
    try {
      const result = await axios.post(
        `${config.backendApi}/content/getUserFavoritesV2`,
        favoriteData
      )

      this._favorites = result.data
      this._favoritesLoading = false
      Minze.cast('lvc:favorites-items-loaded', { data: result.data })
    } catch (error) {
      this._favoritesLoading = false
      Minze.cast('lvc:favorites-items-loading-failed', { error: true })
      return
    }
  }

  public showToastAlert(message: string, timeout: number = 3000, isError?: boolean) {
    const toast = document.createElement('lvc-toast-container')
    toast.setAttribute('data-message', message)
    toast.setAttribute('data-timeout', timeout.toString())
    toast.setAttribute('is-error', isError ? 'true' : 'false')
    document.body.appendChild(toast)
  }

  public async analyticsTrack(eventName: string, data: any) {
    ConsoleInfo(true, 'analyticsTrack', eventName, data)
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: eventName,
      ...data,
    })

    await delay(100)
  }

  public analyticsSetCustomData(data: any = {}) {
    ConsoleInfo(true, 'analyticsSetCustomData', data)
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      ...data,
    })
  }

  /**
   * Track geolocation for analytics (GA4)
   * Call this method on every page view/load to send fresh geolocation data to GTM
   * Wrapped in try-catch to ensure geo failures don't break other functionality
   */
  public async trackGeolocationAnalytics(): Promise<void> {
    try {
      if (!this._geo) {
        this._geo = new Geo(this.getEE(), this)
      }

      this._geolocationAnalyticsScheduled = true
      await this._geo.trackBrowserLocationForAnalytics()
    } catch (error) {
      ConsoleError(true, 'trackGeolocationAnalytics failed (non-blocking)', error)
    }
  }

  private scheduleGeolocationAnalyticsTracking(): void {
    if (this._geolocationAnalyticsScheduled) {
      return
    }

    this._geolocationAnalyticsScheduled = true
    setTimeout(() => {
      this.trackGeolocationAnalytics().catch(error => {
        ConsoleError(true, 'Failed to schedule geolocation analytics tracking', error)
      })
    }, 0)
  }

  public storeEventClickedFromList({ listName, index }: { listName: string; index: string }) {
    window.sessionStorage.setItem(
      'eventClickedFromList',
      JSON.stringify({ listName: listName, index: index })
    )
  }

  private dumpAndClearEventClickedFromList() {
    // Select all elements with the specified data attribute
    const ticketButtons = document.querySelectorAll(
      "[data-gtm-trigger-id='ticketButton']"
    ) as NodeListOf<HTMLElement>
    const eventClickedFromList = window.sessionStorage.getItem('eventClickedFromList')

    // If there are ticket buttons and a eventClickedFromList stored in session storage
    if (ticketButtons.length > 0 && eventClickedFromList) {
      const { listName, index } = JSON.parse(eventClickedFromList)

      // Set data-gtm attributes for each ticket button
      ticketButtons.forEach(button => {
        button.setAttribute('data-gtm-list-name', listName)
        button.setAttribute('data-gtm-list-index', index)
      })
    }

    // Clear the eventClickedFromList from session storage
    window.sessionStorage.removeItem('eventClickedFromList')
  }

  // SATISFACTION POPUP METHODS
  private isSatisfactionPopupEnabled(): boolean {
    // Check config for feature flag
    return config.featureFlags.satisfactionPopup ?? true
  }

  private initializeSatisfactionPopup(): void {
    // Set up cross-tab storage event listener
    window.addEventListener('storage', this.handleSatisfactionStorageEvent.bind(this))
  }

  private handleSatisfactionStorageEvent(event: StorageEvent): void {
    if (event.key === 'lvc:satisfaction_seen' && event.newValue === 'true') {
      // Another tab has shown the popup, cancel this tab's timer
      this.clearSatisfactionPopupTimer()
    }
  }

  private clearSatisfactionPopupTimer(): void {
    if (this._satisfactionPopupTimer !== null) {
      clearTimeout(this._satisfactionPopupTimer)
      this._satisfactionPopupTimer = null
    }
    if (this._satisfactionSecondaryTimer !== null) {
      clearTimeout(this._satisfactionSecondaryTimer)
      this._satisfactionSecondaryTimer = null
    }
  }

  private showSatisfactionPopup(): void {
    // Double-check if already seen to prevent race conditions
    const seenStatus = localStorage.getItem('lvc:satisfaction_seen')
    if (seenStatus === 'true' || this._satisfactionPopupShown) {
      return
    }

    // Mark as seen immediately
    localStorage.setItem('lvc:satisfaction_seen', 'true')
    localStorage.setItem('lvc:satisfaction_opened_at', Date.now().toString())
    this._satisfactionPopupShown = true

    // Inject the popup component
    try {
      this.injectIntoDOM('lvc-satisfaction-popup')
    } catch (error) {
      console.error('Failed to inject satisfaction popup element', error)
    }
  }

  private scheduleSatisfactionPopup(): void {
    // Clear any existing timer
    this.clearSatisfactionPopupTimer()

    // Check if force parameter is present
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('force-satisfaction') === '1') {
      this.showSatisfactionPopup()
      return
    }

    // Check if already seen
    const seenStatus = localStorage.getItem('lvc:satisfaction_seen')
    if (seenStatus === 'true') {
      return
    }

    const now = Date.now()
    const startAt = localStorage.getItem('lvc:satisfaction_start_at')

    if (!startAt) {
      // First time starting the timer
      localStorage.setItem('lvc:satisfaction_start_at', now.toString())
      this._satisfactionPopupTimer = window.setTimeout(() => {
        this.showSatisfactionPopup()
      }, 30000) // 30 seconds for first time
    } else {
      // Timer was already started, calculate remaining time
      const elapsed = now - parseInt(startAt, 10)
      const remaining = 30000 - elapsed
      if (remaining <= 0) {
        // Timer should have already expired - use secondary timer to avoid showing immediately
        this._satisfactionSecondaryTimer = window.setTimeout(() => {
          this.showSatisfactionPopup()
        }, 5000) // 5 seconds secondary timer
      } else {
        // Schedule for remaining time
        this._satisfactionPopupTimer = window.setTimeout(() => {
          this.showSatisfactionPopup()
        }, remaining)
      }
    }
  }

  // Helper method for testing - resets satisfaction popup state
  public resetSatisfactionPopupForTesting(): void {
    localStorage.removeItem('lvc:satisfaction_seen')
    localStorage.removeItem('lvc:satisfaction_start_at')
    localStorage.removeItem('lvc:satisfaction_opened_at')
    this._satisfactionPopupShown = false
    this.clearSatisfactionPopupTimer()
  }

  // Helper method for testing - simulate expired timer for secondary timer testing
  public simulateExpiredTimerForTesting(): void {
    localStorage.removeItem('lvc:satisfaction_seen')
    localStorage.removeItem('lvc:satisfaction_opened_at')
    this._satisfactionPopupShown = false
    // Set start time to 2 minutes ago to simulate expired timer
    const expiredTime = (Date.now() - 120000).toString()
    localStorage.setItem('lvc:satisfaction_start_at', expiredTime)
    this.clearSatisfactionPopupTimer()
  }
}
